// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryDateGroupV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Date eyebrow + a stack of MidasHistoryRowV2 components.
// Date buckets per spec §1: TODAY / YESTERDAY / THIS WEEK / LAST WEEK / EARLIER
//
// Sub-phase 4.5-fix (2026-05-04, D7 RE-REVISED):
//   The original 4.5 implementation locked decision D7 to drop EARLIER and
//   render only TODAY / YESTERDAY / THIS WEEK. User testing on `.cc` with a
//   99-thread account exposed that this hides ~77% of content (any thread
//   older than start-of-this-week silently disappears). D7 has been
//   re-flipped 2026-05-04 to a 5-bucket scheme that mirrors Slack/Discord:
//     TODAY / YESTERDAY / THIS WEEK / LAST WEEK / EARLIER
//   See PHASE4_HISTORY_FIX_SPEC §6 D7 (RE-REVISED 2026-05-04). Future
//   agents must NOT undo this without explicit user instruction.
//
// Sub-phase 4.5 (eyebrow row, 2026-05-04):
//   Eyebrow row layout per `R5-0` designer JSX:
//     [label]  [N chats mono]  [flex-1 hairline #E2DDD3]  [right-aligned date label]
//   Letter-spacing tightened from 0.14em → 0.08em.
//   Date label format:
//     • TODAY     = `Wed, 23 Apr` (today)
//     • YESTERDAY = `Tue, 22 Apr` (yesterday)
//     • THIS WEEK = `17 - 21 Apr` (range: startOfThisWeek …
//                                   startOfYesterday − 1 day);
//                   cross-month variant = `28 Mar - 2 Apr`
//     • LAST WEEK = `10 - 16 Apr` (range: startOfLastWeek … endOfLastWeek);
//                   cross-month variant = `28 Mar - 3 Apr`
//     • EARLIER   = `Before 10 Apr` (open-ended; right-of-cutoff = startOfLastWeek)
//   Locale-aware via i18n.language (en / sk / cs all use Intl short month).
import React, { useMemo } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { Thread } from "../../../midasPageModels";
import MidasHistoryRowV2 from "./MidasHistoryRowV2";

export type DateBucket =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "lastWeek"
  | "earlier";

interface MidasHistoryDateGroupV2Props {
  bucket: DateBucket;
  threads: Thread[];
  /** Map of machineId → display name, built once in the page component. */
  machineNameById: Record<string, string>;
  /** Currently-loaded thread id (or null) — drives row active indicator. */
  activeThreadId: string | null;
  /** Called when a row is clicked. */
  onOpenThread: (threadId: string) => void;
  /** Translation function — uses chatBot namespace keys. */
  t: (key: string, fallback: string) => string;
}

const bucketKey = (b: DateBucket): string => {
  switch (b) {
    case "today":
      return "midas.historyPage.bucketToday";
    case "yesterday":
      return "midas.historyPage.bucketYesterday";
    case "thisWeek":
      return "midas.historyPage.bucketThisWeek";
    case "lastWeek":
      return "midas.historyPage.bucketLastWeek";
    case "earlier":
      return "midas.historyPage.bucketEarlier";
  }
};

const bucketFallback = (b: DateBucket): string => {
  switch (b) {
    case "today":
      return "TODAY";
    case "yesterday":
      return "YESTERDAY";
    case "thisWeek":
      return "THIS WEEK";
    case "lastWeek":
      return "LAST WEEK";
    case "earlier":
      return "EARLIER";
  }
};

// ────────────────────────────────────────────────────────────────
// Date label formatter (sub-phase 4.5 + 4.5-fix)
// ────────────────────────────────────────────────────────────────
//
// TODAY / YESTERDAY → single day in `Wed, 23 Apr` shape.
// THIS WEEK        → range `17 - 21 Apr` (same month) or
//                    `28 Mar - 2 Apr` (cross month).
// LAST WEEK        → range `10 - 16 Apr` (same month) or
//                    `28 Mar - 3 Apr` (cross month).
// EARLIER          → `Before 10 Apr` (open-ended cutoff = startOfLastWeek).
//
// Notes:
//   • Locale comes from i18n.language. Intl handles weekday + month
//     translation natively — no extra locale keys needed.
//   • Bucket windows match `bucketOf` in MidasHistoryPageV2:
//     - thisWeek window: [startOfWeek, startOfYesterday)
//       = startOfWeek = startOfToday − 7 days
//       = endOfRange  = startOfToday − 2 days  (day before yesterday)
//     - lastWeek window: [startOfLastWeek, startOfWeek)
//       = startOfLastWeek = startOfWeek − 7 days
//       = endOfLastWeek   = startOfWeek − 1 day
//     - earlier window: anything older than startOfLastWeek
const formatDateLabel = (
  bucket: DateBucket,
  locale: string,
  beforeLabel: string
): string => {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  if (bucket === "today") {
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(startOfToday);
  }

  if (bucket === "yesterday") {
    const y = new Date(startOfToday);
    y.setDate(y.getDate() - 1);
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(y);
  }

  const dayFmt = new Intl.DateTimeFormat(locale, { day: "numeric" });
  const monthFmt = new Intl.DateTimeFormat(locale, { month: "short" });

  if (bucket === "thisWeek") {
    // thisWeek — range. Window matches bucketOf in MidasHistoryPageV2:
    //   start = startOfWeek = startOfToday − 7 days
    //   end   = startOfYesterday − 1 day  (i.e. the day before yesterday)
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - 7);
    const endOfRange = new Date(startOfToday);
    endOfRange.setDate(endOfRange.getDate() - 2);

    const startDay = dayFmt.format(startOfWeek);
    const endDay = dayFmt.format(endOfRange);
    const startMonth = monthFmt.format(startOfWeek);
    const endMonth = monthFmt.format(endOfRange);

    if (startMonth === endMonth) {
      return `${startDay} - ${endDay} ${endMonth}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
  }

  if (bucket === "lastWeek") {
    // lastWeek — range. Window matches bucketOf in MidasHistoryPageV2:
    //   start = startOfLastWeek = startOfWeek − 7 days
    //   end   = endOfLastWeek   = startOfWeek − 1 day
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - 7);
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    const endOfLastWeek = new Date(startOfWeek);
    endOfLastWeek.setDate(endOfLastWeek.getDate() - 1);

    const startDay = dayFmt.format(startOfLastWeek);
    const endDay = dayFmt.format(endOfLastWeek);
    const startMonth = monthFmt.format(startOfLastWeek);
    const endMonth = monthFmt.format(endOfLastWeek);

    if (startMonth === endMonth) {
      return `${startDay} - ${endDay} ${endMonth}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
  }

  // earlier — open-ended. Cutoff = startOfLastWeek (anything older).
  // Format: `Before 10 Apr` (locale `before` word + day + short month).
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);
  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
  const cutoffLabel = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
  }).format(startOfLastWeek);
  return `${beforeLabel} ${cutoffLabel}`;
};

const MidasHistoryDateGroupV2: React.FC<MidasHistoryDateGroupV2Props> = ({
  bucket,
  threads,
  machineNameById,
  activeThreadId,
  onOpenThread,
  t,
}) => {
  const { getCurrentLanguage } = useTranslation();

  // Memoise label + date — they only change with bucket / language /
  // calendar date, all of which are stable across a typical session.
  const locale = getCurrentLanguage();
  const beforeLabel = t("midas.historyPage.before", "Before");

  const dateLabel = useMemo(
    () => formatDateLabel(bucket, locale, beforeLabel),
    // Cheap to recompute; recompute on bucket / locale change. Calendar-day
    // rollover is uncommon mid-session and not worth a tick.
    [bucket, locale, beforeLabel]
  );

  if (threads.length === 0) return null;

  const label = t(bucketKey(bucket), bucketFallback(bucket));

  // Plural for "chat" — singular "1 chat" vs plural "N chats".
  // Uses dedicated `chatOne` / `chatMany` keys so non-EN locales can
  // adopt their own plural rules.
  const count = threads.length;
  const countWord =
    count === 1
      ? t("midas.historyPage.chatOne", "chat")
      : t("midas.historyPage.chatMany", "chats");

  return (
    // §5.4 audit: eyebrow → first-row gap bumped 8px → 16px (gap-2 → gap-4)
    // so the date label has the air the spec shows above the first card.
    <section aria-label={label} className="flex flex-col gap-4">
      {/* Eyebrow row (sub-phase 4.5):
            [label] [N chats] [flex-1 hairline] [right-aligned date label]
          See `R5_0_DESIGNER_JSX.md` § "DATE GROUP".
          Sub-phase 4.6 §3H.2: soft `headerReveal` entry — fade-in + small Y
          translate. Shared keyframe injected by `useRedesignSetup` (page-level
          hook called by MidasHistoryPageV2). Reduced-motion users see no
          animation. Inline fontFamily styles dropped — `font-mono` Tailwind
          class binds JetBrains Mono via tailwind.config.js fontFamily.mono. */}
      <div
        className="flex items-baseline gap-3 pb-2.5 motion-reduce:[animation:none!important]"
        style={{ animation: "headerReveal 360ms ease-out both" }}
      >
        <h2 className="font-mono text-[11px] font-medium uppercase leading-4 tracking-[0.08em] text-[#8C8375]">
          {label}
        </h2>
        <span className="font-mono text-[10px] leading-3 tracking-[0.08em] text-[#9A9186]">
          {`${count} ${countWord}`}
        </span>
        <span
          aria-hidden="true"
          className="h-px shrink grow basis-0 bg-[#E2DDD3]"
        />
        <span className="font-mono text-[10px] leading-3 text-[#9A9186]">
          {dateLabel}
        </span>
      </div>
      {/* Sub-phase 4.4a: rows are now hairline-bottom (no card border), so
          the previous `gap-3` between rows is gone — rows sit edge-to-edge
          and the 1px hairline separates them. List has no padding/margin
          of its own; rows own their own inset (px-3 py-3.5). */}
      <ul className="m-0 flex list-none flex-col p-0">
        {threads.map((thread, idx) => {
          const machineLabel = thread.machine_id
            ? machineNameById[thread.machine_id] || null
            : null;
          return (
            <MidasHistoryRowV2
              key={thread.id}
              thread={thread}
              machineLabel={machineLabel}
              activeThreadId={activeThreadId}
              onOpen={onOpenThread}
              t={t}
              // Sub-phase 4.6 §3H.2: per-group stagger (each row delayed by
              // `idx * 0.04s` — 40ms cascade). Group-relative is intentional;
              // a global counter would over-stagger after Load more.
              index={idx}
            />
          );
        })}
      </ul>
    </section>
  );
};

export default MidasHistoryDateGroupV2;
